import apiClient from './client'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  User,
  Camera,
  CameraMapPin,
  CameraCreateRequest,
  CameraUpdateRequest,
  CameraGroup,
  CameraGroupCreateRequest,
  Event,
  EventFilters,
  Media,
  MediaFilters,
  Notification,
  NotificationSettings,
  AIModel,
  DetectionClass,
  PaginatedResponse,
  NotificationContact,
  NotificationContactCreate,
  ContactCameraLink,
  SIMUsage,
  AdminUser,
  SimMismatch,
  SimAudit,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  TestCamera,
  TestCameraDetail,
  TestMediaUpload,
  AnnotationProject,
  AnnotationTask,
  AnnotationTaskDetail,
  AnnotationBBox,
  ImportFilters,
  ImportResult,
  ImportPreview,
  CameraExchangeLog,
  AudioConfigResponse,
  AudioConfigPatch,
  DetectorConfigsResponse,
  DetectorConfigPatch,
  GlobalThresholdsResponse,
  GlobalThresholdPatch,
} from '@/types'

// ============================================================================
// Authentication API
// ============================================================================

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', data)
    return response.data
  },

  register: async (data: RegisterRequest): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/register', data)
    return response.data
  },

  logout: async (fcmToken?: string): Promise<void> => {
    await apiClient.post('/auth/logout', { fcm_token: fcmToken })
  },

  refreshToken: async (refreshToken: string): Promise<{ access: string; refresh: string }> => {
    const response = await apiClient.post('/auth/refresh-token', { refresh: refreshToken })
    return response.data
  },

  verifyEmail: async (email: string, code: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/verify-email', { email, code })
    return response.data
  },

  resendOtp: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-otp', { email })
    return response.data
  },

  requestPasswordReset: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/password-reset/request', { email })
    return response.data
  },

  confirmPasswordReset: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/password-reset/confirm', {
      email,
      code,
      new_password: newPassword,
    })
    return response.data
  },
}

// ============================================================================
// User API
// ============================================================================

export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/user/profile')
    return response.data
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.put('/user/profile', data)
    return response.data
  },

  deleteAccount: async (password: string): Promise<void> => {
    await apiClient.delete('/user/delete', { data: { password } })
  },
}

// ============================================================================
// Cameras API
// ============================================================================

export const camerasApi = {
  getAll: async (search?: string): Promise<Camera[]> => {
    const params: Record<string, string | number> = { page_size: 1000 }
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  // Superuser-only: every camera across all owners (#095). The backend ignores
  // scope=all for non-superusers, so this is safe to call only from superuser-gated UI.
  getAllAdmin: async (userId?: number, search?: string): Promise<Camera[]> => {
    const params: Record<string, string | number> = { scope: 'all', page_size: 1000 }
    if (userId) params.user_id = userId
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    return response.data.results ?? response.data
  },

  getPaginated: async (
    page = 1,
    search?: string
  ): Promise<PaginatedResponse<Camera>> => {
    const params: Record<string, string | number> = { page }
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    return response.data
  },

  getInfinite: async (
    page = 1,
    search?: string,
    group?: string,
    ingestionType?: 'http' | 'ftp',
    onlineStatus?: 'online' | 'offline' | 'unknown',
    batteryLow?: boolean,
  ): Promise<PaginatedResponse<Camera>> => {
    const params: Record<string, string | number> = { page }
    if (search) params.search = search
    if (group && group !== 'all') params.group = group
    if (ingestionType) params.ingestion_type = ingestionType
    if (onlineStatus) params.online_status = onlineStatus
    if (batteryLow) params.battery_low = 'true'
    const response = await apiClient.get('/cameras/', { params })
    return response.data
  },

  getById: async (id: number): Promise<Camera> => {
    const response = await apiClient.get(`/cameras/${id}/`)
    return response.data
  },

  create: async (data: CameraCreateRequest): Promise<Camera> => {
    const response = await apiClient.post('/cameras/', data)
    return response.data
  },

  update: async (id: number, data: CameraUpdateRequest): Promise<Camera> => {
    const response = await apiClient.patch(`/cameras/${id}/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/cameras/${id}/`)
  },

  getMapPins: async (): Promise<CameraMapPin[]> => {
    const response = await apiClient.get('/cameras/map-pins/')
    return response.data
  },

  getSimUsage: async (): Promise<{ usage: Record<string, SIMUsage>; cached: boolean; count: number }> => {
    const response = await apiClient.get('/cameras/sim-usage/')
    return response.data
  },

  getSimCardStatus: async (id: number): Promise<{ card_info: Record<string, unknown>; iccid: string }> => {
    const response = await apiClient.get(`/cameras/${id}/sim-card-status/`)
    return response.data
  },

  getStatus: async (id: number): Promise<{ is_online: boolean | null; last_seen: string | null }> => {
    const response = await apiClient.get(`/cameras/${id}/status/`)
    return response.data
  },

  getSharedUsers: async (id: number): Promise<User[]> => {
    const response = await apiClient.get(`/cameras/${id}/shared-users/`)
    return response.data
  },

  shareCamera: async (id: number, email: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/cameras/${id}/share-camera/`, { email })
    return response.data
  },

  unshareCamera: async (id: number, userId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/cameras/${id}/unshare-camera/`, { user_id: userId })
    return response.data
  },

  getAvailableClasses: async (id: number): Promise<{ camera_id: number; available_class_names: string[] }> => {
    const response = await apiClient.get(`/cameras/${id}/available-classes/`)
    return response.data
  },

  updatePushSettings: async (id: number, data: { push_enabled?: boolean; push_class_names?: string[] }): Promise<{ id: number; push_enabled: boolean; push_class_names: string[] }> => {
    const response = await apiClient.patch(`/cameras/${id}/push-settings/`, data)
    return response.data
  },

  getAudioClasses: async (id: number): Promise<AudioConfigResponse> => {
    const response = await apiClient.get(`/cameras/${id}/audio-classes/`)
    return response.data
  },

  updateAudioClassConfig: async (id: number, patch: AudioConfigPatch): Promise<AudioConfigResponse> => {
    const response = await apiClient.patch(`/cameras/${id}/audio-classes/`, patch)
    return response.data
  },

  getAiModelConfigs: async (id: number): Promise<DetectorConfigsResponse> => {
    const response = await apiClient.get(`/cameras/${id}/ai-model-configs/`)
    return response.data
  },

  updateAiModelConfigs: async (id: number, patch: DetectorConfigPatch): Promise<DetectorConfigsResponse> => {
    const response = await apiClient.patch(`/cameras/${id}/ai-model-configs/`, patch)
    return response.data
  },

  resetAiModelConfigs: async (id: number): Promise<{ cleared: number }> => {
    const response = await apiClient.post(`/cameras/${id}/ai-model-configs/reset/`)
    return response.data
  },
}

// ============================================================================
// Camera Groups API
// ============================================================================

export const cameraGroupsApi = {
  getAll: async (): Promise<CameraGroup[]> => {
    const response = await apiClient.get('/camera-groups/')
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  getById: async (id: number): Promise<CameraGroup> => {
    const response = await apiClient.get(`/camera-groups/${id}/`)
    return response.data
  },

  create: async (data: CameraGroupCreateRequest): Promise<CameraGroup> => {
    const response = await apiClient.post('/camera-groups/', data)
    return response.data
  },

  update: async (id: number, name: string): Promise<CameraGroup> => {
    const response = await apiClient.put(`/camera-groups/${id}/`, { name })
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/camera-groups/${id}/`)
  },

  addCameras: async (id: number, cameraIds: number[]): Promise<CameraGroup> => {
    const response = await apiClient.post(`/camera-groups/${id}/add-cameras/`, {
      camera_ids: cameraIds,
    })
    return response.data
  },

  removeCameras: async (id: number, cameraIds: number[]): Promise<CameraGroup> => {
    const response = await apiClient.post(`/camera-groups/${id}/remove-cameras/`, {
      camera_ids: cameraIds,
    })
    return response.data
  },

  // Group sharing (#096) — share a whole group by email; recipient gets
  // read-only access to the group's cameras (+ push), not email/SMS.
  shareGroup: async (id: number, email: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/camera-groups/${id}/share/`, { email })
    return response.data
  },

  unshareGroup: async (id: number, userId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/camera-groups/${id}/unshare/`, { user_id: userId })
    return response.data
  },

  getGroupSharedUsers: async (
    id: number,
  ): Promise<{ owner: { id: number; username: string; email: string } | null; shared_users: User[]; total_users: number }> => {
    const response = await apiClient.get(`/camera-groups/${id}/shared-users/`)
    return response.data
  },
}

// ============================================================================
// Events API
// ============================================================================

export const eventsApi = {
  getAll: async (
    filters?: EventFilters,
    page = 1
  ): Promise<PaginatedResponse<Event>> => {
    const response = await apiClient.get('/events/', { params: { ...filters, page } })
    return response.data
  },

  getById: async (id: number): Promise<Event> => {
    const response = await apiClient.get(`/events/${id}/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/events/${id}/`)
  },

  // Bulk-delete events in one request (#097). Owner-only per id, max 500.
  // Returns a partial result so one bad id never fails the whole batch.
  bulkDelete: async (
    ids: number[],
  ): Promise<{ deleted: number[]; failed: Array<{ id: number; reason: string }>; s3_failed: number }> => {
    const response = await apiClient.post('/events/bulk-delete/', { ids })
    return response.data
  },

  markAsRead: async (id: number): Promise<Event> => {
    const response = await apiClient.post(`/events/${id}/read/`)
    return response.data
  },
}

// ============================================================================
// Media API
// ============================================================================

export const mediaApi = {
  getAll: async (
    filters?: MediaFilters,
    page = 1
  ): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get('/media/', { params: { ...filters, page } })
    return response.data
  },

  getById: async (id: number): Promise<Media> => {
    const response = await apiClient.get(`/media/${id}/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/media/${id}/`)
  },

  search: async (
    filters: MediaFilters,
    page = 1
  ): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get('/media/search/', { params: { ...filters, page } })
    return response.data
  },
}

// ============================================================================
// Notifications API
// ============================================================================

export const notificationsApi = {
  getAll: async (page = 1): Promise<PaginatedResponse<Notification>> => {
    const response = await apiClient.get('/notifications/', { params: { page } })
    return response.data
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get('/notifications/unread/')
    return response.data
  },

  markAsRead: async (id: number): Promise<Notification> => {
    const response = await apiClient.post(`/notifications/${id}/read/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/notifications/${id}/`)
  },

  getSettings: async (): Promise<NotificationSettings> => {
    const response = await apiClient.get('/notifications/settings/')
    return response.data
  },

  updateSettings: async (settings: Partial<NotificationSettings>): Promise<NotificationSettings> => {
    const response = await apiClient.post('/notifications/settings/', settings)
    return response.data
  },
}

// ============================================================================
// AI Models API
// ============================================================================

export const aiModelsApi = {
  getAll: async (): Promise<AIModel[]> => {
    const response = await apiClient.get('/ai-models/')
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  getById: async (id: number): Promise<AIModel> => {
    const response = await apiClient.get(`/ai-models/${id}/`)
    return response.data
  },

  getDetectionClasses: async (baseModelId?: number): Promise<DetectionClass[]> => {
    const params = baseModelId ? { base_model: baseModelId } : {}
    const response = await apiClient.get('/detection-classes/', { params })
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  getGlobalThresholds: async (): Promise<GlobalThresholdsResponse> => {
    const response = await apiClient.get('/ai-models/global-thresholds/')
    return response.data
  },

  updateGlobalThreshold: async (patch: GlobalThresholdPatch): Promise<GlobalThresholdsResponse> => {
    const response = await apiClient.patch('/ai-models/global-thresholds/', patch)
    return response.data
  },

  resetCameraThresholds: async (): Promise<{ cleared: number }> => {
    const response = await apiClient.post('/ai-models/reset-camera-thresholds/')
    return response.data
  },
}

// ============================================================================
// Notification Contacts API
// ============================================================================

export const notificationContactsApi = {
  getAll: async (): Promise<NotificationContact[]> => {
    const response = await apiClient.get('/notification-contacts/')
    return response.data.results ?? response.data
  },

  create: async (data: NotificationContactCreate): Promise<NotificationContact> => {
    const response = await apiClient.post('/notification-contacts/', data)
    return response.data
  },

  update: async (id: number, data: { first_name?: string; last_name?: string; label?: string }): Promise<NotificationContact> => {
    const response = await apiClient.patch(`/notification-contacts/${id}/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/notification-contacts/${id}/`)
  },

  verify: async (id: number, code: string): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/verify/`, { code })
    return response.data
  },

  resendOtp: async (id: number): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/resend-otp/`)
    return response.data
  },

  getCameras: async (id: number): Promise<ContactCameraLink[]> => {
    const response = await apiClient.get(`/notification-contacts/${id}/cameras/`)
    return response.data
  },

  linkCameras: async (id: number, camera_ids: number[]): Promise<{ detail: string; linked_camera_ids: number[] }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/cameras/`, { camera_ids })
    return response.data
  },

  unlinkCamera: async (id: number, cameraId: number): Promise<void> => {
    await apiClient.delete(`/notification-contacts/${id}/cameras/${cameraId}/`)
  },

  getCameraAvailableClasses: async (contactId: number, cameraId: number): Promise<{ camera_id: number; available_class_names: string[] }> => {
    const response = await apiClient.get(`/notification-contacts/${contactId}/cameras/${cameraId}/available-classes/`)
    return response.data
  },

  updateCameraClasses: async (contactId: number, cameraId: number, alertClassNames: string[]): Promise<ContactCameraLink> => {
    const response = await apiClient.patch(`/notification-contacts/${contactId}/cameras/${cameraId}/classes/`, { alert_class_names: alertClassNames })
    return response.data
  },
}

// ============================================================================
// Admin User Management API
// ============================================================================

export const adminApi = {
  listUsers: async (): Promise<AdminUser[]> => {
    const response = await apiClient.get('/admin/users/')
    // DefaultRouter wraps in pagination — unwrap if needed
    return Array.isArray(response.data) ? response.data : (response.data.results ?? [])
  },

  createUser: async (data: AdminUserCreateRequest): Promise<AdminUser> => {
    const response = await apiClient.post('/admin/users/', data)
    return response.data
  },

  updateUser: async (id: number, data: AdminUserUpdateRequest): Promise<AdminUser> => {
    const response = await apiClient.patch(`/admin/users/${id}/`, data)
    return response.data
  },

  deleteUser: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/users/${id}/`)
  },

  // #076 — SIM mismatch review (superuser-only endpoints)
  listSimMismatches: async (): Promise<SimMismatch[]> => {
    const response = await apiClient.get('/admin/cameras/sim-mismatches/')
    return response.data.results ?? []
  },

  approveReportedImsi: async (cameraId: number, reason?: string): Promise<void> => {
    await apiClient.post(`/admin/cameras/${cameraId}/approve-reported-imsi/`, { reason })
  },

  replaceImsi: async (cameraId: number, newImsi: string, reason?: string): Promise<void> => {
    await apiClient.post(`/admin/cameras/${cameraId}/replace-imsi/`, { new_imsi: newImsi, reason })
  },

  getSimAudit: async (cameraId: number): Promise<SimAudit[]> => {
    const response = await apiClient.get(`/admin/cameras/${cameraId}/sim-audit/`)
    return response.data.results ?? []
  },
}

// ============================================================================
// System Settings API
// ============================================================================

export const systemSettingsApi = {
  getAlertLinkExpiry: async (): Promise<{ expiry_hours: number | null }> => {
    const response = await apiClient.get('/system-settings/alert-link-expiry/')
    return response.data
  },

  setAlertLinkExpiry: async (expiry_hours: number | null): Promise<{ expiry_hours: number | null }> => {
    const response = await apiClient.patch('/system-settings/alert-link-expiry/', { expiry_hours })
    return response.data
  },
}

// ============================================================================
// Test Cameras API (admin only — camera_test app)
// ============================================================================

// ============================================================================
// Annotations API (admin only)
// ============================================================================

export const annotationsApi = {
  // Projects
  listProjects: async (): Promise<PaginatedResponse<AnnotationProject>> => {
    const response = await apiClient.get('/annotations/projects/')
    return response.data
  },

  getProject: async (id: number): Promise<AnnotationProject> => {
    const response = await apiClient.get(`/annotations/projects/${id}/`)
    return response.data
  },

  createProject: async (data: { name: string; description?: string; class_set: { id: number; name: string; color: string }[] }): Promise<AnnotationProject> => {
    const response = await apiClient.post('/annotations/projects/', data)
    return response.data
  },

  updateProject: async (id: number, data: Partial<{ name: string; description: string; class_set: { id: number; name: string; color: string }[] }>): Promise<AnnotationProject> => {
    const response = await apiClient.patch(`/annotations/projects/${id}/`, data)
    return response.data
  },

  deleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(`/annotations/projects/${id}/`)
  },

  // Import
  importPreview: async (projectId: number, filters: ImportFilters): Promise<ImportPreview> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/import-preview/`, filters)
    return response.data
  },

  importFromPipeline: async (projectId: number, filters: ImportFilters): Promise<ImportResult> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/import/`, filters)
    return response.data
  },

  // Available options for import filters
  availableClasses: async (): Promise<{ classes: string[] }> => {
    const response = await apiClient.get('/annotations/projects/available-classes/')
    return response.data
  },

  availableCameras: async (): Promise<{ cameras: { id: number; name: string; imei: string }[] }> => {
    const response = await apiClient.get('/annotations/projects/available-cameras/')
    return response.data
  },

  // Export
  exportYolo: async (projectId: number, filters: {
    classes?: string[]
    cameras?: string[]
    date_from?: string | null
    date_to?: string | null
    status?: string[]
    train_split?: number
    include_all_labels?: boolean
  }): Promise<{ download_url: string }> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/export/yolo/`, filters)
    return response.data
  },

  // Tasks
  listTasks: async (projectId: number, params?: { status?: string; camera?: string; page?: number }): Promise<PaginatedResponse<AnnotationTask>> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/`, { params })
    return response.data
  },

  getTask: async (projectId: number, taskId: number): Promise<AnnotationTaskDetail> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/${taskId}/`)
    return response.data
  },

  updateTask: async (projectId: number, taskId: number, data: Partial<{ status: string; assigned_to: number | null }>): Promise<AnnotationTask> => {
    const response = await apiClient.patch(`/annotations/projects/${projectId}/tasks/${taskId}/`, data)
    return response.data
  },

  saveAnnotations: async (projectId: number, taskId: number, data: { annotations: Omit<AnnotationBBox, 'id' | 'created_by' | 'created_at'>[]; status?: string }): Promise<AnnotationTaskDetail> => {
    const response = await apiClient.put(`/annotations/projects/${projectId}/tasks/${taskId}/annotations/`, data)
    return response.data
  },

  getNeighbors: async (projectId: number, taskId: number): Promise<{ prev_id: number | null; next_id: number | null }> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/${taskId}/neighbors/`)
    return response.data
  },
}

export const testCamerasApi = {
  list: async (): Promise<TestCamera[]> => {
    const response = await apiClient.get('/test-cameras/')
    return response.data
  },

  detail: async (imei: string): Promise<TestCameraDetail> => {
    const response = await apiClient.get(`/test-cameras/${imei}/`)
    return response.data
  },

  media: async (imei: string): Promise<TestMediaUpload[]> => {
    const response = await apiClient.get(`/test-cameras/${imei}/media/`)
    return response.data
  },
}

// ============================================================================
// Camera Exchange Logs API (#059 — superuser only; IsAdminUser on the backend)
// ============================================================================

export const cameraExchangeLogsApi = {
  list: async (params: { imei?: string; kind?: string; camera?: number; page?: number }) => {
    const response = await apiClient.get('/admin/camera-exchange-logs/', { params })
    return response.data as PaginatedResponse<CameraExchangeLog>
  },
}

// ============================================================================
// Google Maps Platform (#080)
// ============================================================================

export { mapsApi } from './maps'
